/**
 * Display labels.
 *
 * The rule-pack request types are precise but long — "Access request
 * (grant/add/share)", "Account lifecycle: exit / offboarding / transfer" — and
 * in a table they wrap to three lines and break mid-word, which is most of why
 * a dense list looks unfinished.
 *
 * So: a short label for the cell, the full label in a tooltip, and the full
 * label everywhere there is room. The mapping is explicit rather than a
 * truncation heuristic, because "Account lifecycle: new…" is not a label a
 * person would ever write.
 */

const SHORT: Record<string, string> = {
  'Access request (grant/add/share)': 'Access request',
  'Account lifecycle: new hire / onboarding': 'Onboarding',
  'Account lifecycle: exit / offboarding / transfer': 'Offboarding',
  'Document formatting / editing (Pubs)': 'Doc formatting',
  'Publication production / packaging': 'Publication packaging',
  'Software install / upgrade / license': 'Software install',
  'Distribution list / group / mailbox change': 'Distribution list',
  'SaaS account provisioning (add user to app)': 'SaaS provisioning',
  'External collaboration / guest access': 'Guest access',
  'Travel / vehicle / facilities booking': 'Travel booking',
  'Facilities / office / event support': 'Facilities',
  'Safety / H&S program request': 'H&S request',
  'Security report / suspicious message': 'Security report',
  'Employee info / directory update': 'Directory update',
  'Password reset / MFA / lockout': 'Password / MFA',
  'Data / file recovery / restore': 'File recovery',
  'Break/fix troubleshooting': 'Break/fix',
  'How-to / information question': 'How-to',
  'Hardware request / replacement': 'Hardware',
  'Performance / slowness': 'Performance',
  'Shipping / mailing / fax': 'Shipping',
  'Finance / AP / expense': 'Finance / AP',
  'Procurement / purchase request': 'Procurement',
  Unclassified: 'Unclassified',
};

export function shortRequestType(label: string | null | undefined): string {
  if (!label) return '—';
  return SHORT[label] ?? label;
}

/** A stable, muted category for a request type, used for the type chip. */
export function requestTypeGroup(label: string | null | undefined): string {
  if (!label) return 'other';
  const l = label.toLowerCase();
  if (/access|password|mfa|onboarding|offboarding|directory|distribution|saas|guest/.test(l)) {
    return 'identity';
  }
  if (/break\/fix|hardware|performance|install|recovery/.test(l)) return 'device';
  if (/security|suspicious/.test(l)) return 'security';
  if (/formatting|publication/.test(l)) return 'publishing';
  if (/travel|facilit|shipping|procurement|finance/.test(l)) return 'business';
  if (/safety|h&s/.test(l)) return 'safety';
  return 'other';
}

/** Queue names are short already, but a couple wrap in a 130px column. */
export function shortQueue(name: string | null | undefined): string {
  if (!name) return '—';
  return name.replace(' & ', ' & ').replace('Formatting & Editing', 'Formatting');
}
